import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, CornerDownRight } from 'lucide-react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { Banner } from 'slab'

import { BenchmarkList } from './BenchmarkList'
import { type GenerateQuoteFormProps, type Quote } from './types'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { useGenerateQuote } from '../hooks/useGenerateQuote'
import {
  FormDateField,
  FormMultiselectField,
  FormSelectField,
  FormTextField,
} from '../reusable/form/FormFields'

type Props = {
  prospectId: string;
  quoteId: string;
  onGoBackToBenchmarkParameters: () => void;
  onSuccess: () => void;
  onFailure: (error: GrapeAPIErrorType) => void;
  enableQuotingComplianceEscapeHatch?: boolean;
}

const METAL_TIER_OPTIONS = [
  { label: 'Bronze', value: 'bronze' },
  { label: 'Silver', value: 'silver' },
  { label: 'Gold', value: 'gold' },
  { label: 'Platinum', value: 'platinum' },
]

export function GenerateQuoteForm ({
  prospectId,
  quoteId,
  quote,
  onGoBackToBenchmarkParameters,
  onSuccess,
  onFailure,
  readOnly,
  enableQuotingComplianceEscapeHatch = false,
}: Props & {
  quote?: Quote;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient()

  const generateQuoteMutation = useGenerateQuote({ prospectId, quoteId })

  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<GenerateQuoteFormProps>({
    defaultValues: {
      metal_tiers:
        quote && readOnly
          ? quote.metal_tiers?.join(', ') || ''
          : quote?.metal_tiers || [],
      coverage_percentiles: quote?.coverage_percentiles
        ? quote?.coverage_percentiles?.join('%, ') + '%'
        : '1%, 25%, 50%, 75%, 90%, 100%',
      coverage_start_date: quote?.coverage_start_date ?? undefined,
      year: quote?.year ?? undefined,
      affordability_strategy: 'floor_mec',
    },
  })

  const watchedAffordabilityStrategy = watch('affordability_strategy')

  const onSubmit: SubmitHandler<GenerateQuoteFormProps> = (data) => {
    // check coverage start date
    if (new Date(data.coverage_start_date) < new Date()) {
      setError('coverage_start_date', {
        type: 'manual',
        message: 'Coverage start date must be in the future.',
      })
      return
    }

    generateQuoteMutation.mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['prospect', prospectId, 'quote', quoteId],
        })
        onSuccess()
      },
      onError: (error) => {
        onFailure(error)
      },
    })
  }

  const metalTiersField = readOnly
    ? (
      <FormTextField<GenerateQuoteFormProps>
        disabled
        fieldId='metal_tiers'
        fieldLabel='Metal tiers'
        register={register}
      />
      )
    : (
      <FormMultiselectField<GenerateQuoteFormProps>
        control={control}
        error={errors.metal_tiers}
        fieldId='metal_tiers'
        fieldLabel='Metal tiers'
        fieldOptions={METAL_TIER_OPTIONS}
      />
      )
  const coveragePercentilesField = (
    <FormTextField<GenerateQuoteFormProps>
      disabled={generateQuoteMutation.isPending || readOnly}
      error={errors.coverage_percentiles}
      fieldId='coverage_percentiles'
      fieldLabel='Percentiles'
      register={register}
      validationRule={{
        required: 'Percentiles field is required.',
        setValueAs: (str: string) =>
          str
            ?.replace(/%/g, '')
            .split(/%,\s*|,/)
            .map((s) => s.trim()),
        checkInteger: (value: string[]) => {
          const errors = value.map((v) => {
            if (isNaN(parseInt(v))) {
              return 'Percentile must be an integer.'
            }
            return null
          })
          if (errors.some((e) => e !== null)) {
            return errors
          }
        },
        checkDuplicate: (value: string[]) => {
          const unique = new Set(value)
          if (unique.size !== value.length) {
            return 'Percentiles must be unique.'
          }
        },
      }}
    />
  )
  const coverageStartDateField = (
    <FormDateField<GenerateQuoteFormProps>
      disabled={generateQuoteMutation.isPending || readOnly}
      error={errors.coverage_start_date}
      fieldId='coverage_start_date'
      fieldLabel='Coverage start date'
      register={register}
      validationRule={{
        required: 'Coverage start date is required.',
      }}
    />
  )

  const quoteYearField = (
    <FormSelectField<GenerateQuoteFormProps>
      disabled={generateQuoteMutation.isPending || readOnly}
      error={errors.year}
      fieldId='year'
      fieldLabel='Quote year'
      fieldOptions={[
        { label: '2026', value: 2026 },
        { label: '2025', value: 2025 },
        { label: '2024', value: 2024 },
      ]}
      register={register}
      validationRule={{
        required: 'Quote year is required.',
      }}
    />
  )

  const affordabilityStrategyField = enableQuotingComplianceEscapeHatch
    ? (
      <FormSelectField<GenerateQuoteFormProps>
        disabled={generateQuoteMutation.isPending || readOnly}
        error={errors.affordability_strategy}
        fieldId='affordability_strategy'
        fieldLabel='Affordability strategy'
        fieldOptions={[
          { label: 'Use Lowest Earner per Rating Area', value: 'floor_mec' },
          { label: 'Use Individual Employee Salaries', value: 'individual_mec' },
        ]}
        register={register}
        validationRule={{
          required: 'Affordability strategy is required.',
        }}
      />
      )
    : null

  const formClass = readOnly
    ? 'flex flex-col justify-between gap-y-4 mt-6'
    : 'space-y-4'

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={formClass}>
      {!readOnly
        ? (
          <>
            <div className='flex gap-x-4 items-center'>
              {metalTiersField}
              {coveragePercentilesField}
              {coverageStartDateField}
              {quoteYearField}
            </div>
            {affordabilityStrategyField && (
              <div className='flex gap-x-4 items-center'>
                {affordabilityStrategyField}
              </div>
            )}
            {watchedAffordabilityStrategy === 'individual_mec' && (
              <Banner
                type='warning'
                title='Warning'
              >
                This setting can reduce allowances, but also increases the risk of new hires receiving non-compliant allowances if their salaries are significantly lower than the census employees used to build allowances per rating-area. This would be a legal & reputational risk for Thatch, so please be careful!
              </Banner>
            )}
          </>
          )
        : (
          <>
            <div className='flex gap-x-4 justify-between'>
              {metalTiersField}
              {coveragePercentilesField}
            </div>
            <div className='flex gap-x-4 justify-between'>
              {coverageStartDateField}
              {quoteYearField}
            </div>
            {affordabilityStrategyField && (
              <div className='flex gap-x-4 justify-between'>
                {affordabilityStrategyField}
              </div>
            )}
          </>
          )}
      {!readOnly && (
        <div className='flex items-center gap-x-2'>
          <button
            className='btn-outline'
            type='button'
            onClick={onGoBackToBenchmarkParameters}
          >
            <ChevronLeft size={16} className='mr-1' />
            Back
          </button>
          <button className='btn-primary'>
            Create Quote
            <CornerDownRight size={16} className='ml-1' />
          </button>
        </div>
      )}
    </form>
  )
}

export function QuoteCreateGenerateQuote ({
  prospectId,
  quoteId,
  skipBenchmark = false,
  onGoBackToBenchmarkParameters,
  onSuccess,
  onFailure,
  enableQuotingComplianceEscapeHatch = false,
}: Props & {
  skipBenchmark?: boolean;
}) {
  return (
    <>
      {!skipBenchmark && (
        <BenchmarkList prospectId={prospectId} quoteId={quoteId} />
      )}
      <GenerateQuoteForm
        prospectId={prospectId}
        quoteId={quoteId}
        onGoBackToBenchmarkParameters={onGoBackToBenchmarkParameters}
        onSuccess={onSuccess}
        onFailure={onFailure}
        enableQuotingComplianceEscapeHatch={enableQuotingComplianceEscapeHatch}
      />
    </>
  )
}
